/**
 * =============================================================================
 * SPEQ55 RFP Pro - Parameter Sets List Page
 * =============================================================================
 *
 * WHAT THIS FILE DOES
 * -------------------
 * Renders the /app/parameter-sets page: the main entry point for managing
 * reusable collections of keywords and technical specs that users apply
 * to RFP analyses. Parameter sets are grouped by visibility so the user
 * understands at a glance who else can see each set:
 *
 *     ┌─ Organization Defaults ───────────────────────┐
 *     │   (seeded "Standard RFP Analysis" + any       │
 *     │    promoted sets; visible to everyone in org) │
 *     ├─ Shared in Organization ──────────────────────┤
 *     │   (private sets that a creator flipped to     │
 *     │    shared; visible to everyone, editable only │
 *     │    by the creator or an org admin)            │
 *     ├─ My Private Sets ─────────────────────────────┤
 *     │   (only visible to the current user)          │
 *     └───────────────────────────────────────────────┘
 *
 * WHY IT EXISTS
 * -------------
 * Before parameter sets, users had to manually re-select keywords/specs
 * every time they ran an analysis. This page is where they curate and
 * reuse those collections. The backend enforces visibility rules — this
 * page only presents the matching UI affordances so users aren't shown
 * buttons (like Promote) that the server would reject anyway.
 *
 * HOW IT WORKS
 * ------------
 * 1. On mount: GET /api/parameter-sets (via listParameterSets()). The
 *    backend returns EVERYTHING visible to the user in a single payload.
 * 2. Client-side we bucket the rows by `visibility` (org_default / shared
 *    / private) so each section only renders once.
 * 3. Action buttons (Edit, Delete, Share/Unshare, Promote) are rendered
 *    conditionally based on three predicates:
 *       - isOwner(set)      : creator of the set (for share/unshare/delete)
 *       - canEdit(set)      : creator OR org_admin of org_default
 *       - canPromote        : current user is org_admin+ (for promote)
 * 4. All destructive/transition actions go through a confirmation modal
 *    with the `confirm` state object: { type, set }.
 *
 * DEPENDENCIES
 * ------------
 *   react-router-dom       - Link, useNavigate for navigation
 *   ../../api/parameterSetsApi
 *                          - thin axios wrappers around /api/parameter-sets
 *   ../../contexts/AuthContext
 *                          - useAuth() provides user + hasMinRole helper
 *
 * DEPENDED ON BY
 * --------------
 *   frontend/src/App.jsx   - mounted at /app/parameter-sets
 *   frontend/src/components/layout/Layout.jsx
 *                          - sidebar entry linking here
 *
 * @module pages/parameter-sets/ParameterSetsList
 * @author SPEQ55
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  listParameterSets,
  deleteParameterSet,
  shareParameterSet,
  unshareParameterSet,
  promoteParameterSet,
} from '../../api/parameterSetsApi';
import { useAuth } from '../../contexts/AuthContext';

const VISIBILITY_SECTIONS = [
  {
    key: 'org_default',
    title: 'Organization Defaults',
    description: 'Standard sets available to everyone in your organization.',
  },
  {
    key: 'shared',
    title: 'Shared in Organization',
    description: 'Sets an org member has chosen to share with teammates.',
  },
  {
    key: 'private',
    title: 'My Private Sets',
    description: 'Personal sets visible only to you.',
  },
];

function ParameterSetsList() {
  const navigate = useNavigate();
  const { user, hasMinRole } = useAuth();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  //   sets         - flat array of every set returned by the API (all scopes)
  //   loading      - true while the initial (or reload) fetch is in flight
  //   error        - top-level error banner (covers the whole page)
  //   actionError  - error shown inside the confirmation modal (scoped to the
  //                  modal so the rest of the page stays interactive)
  //   confirm      - null when no modal open, otherwise { type, set } where
  //                  type ∈ { 'delete', 'share', 'unshare', 'promote' }
  // ---------------------------------------------------------------------------
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type, set }

  // org_admin or higher can promote shared sets to org_default. We compute
  // this once per render and reuse it in both the "Promote" button predicate
  // and the canEdit() predicate for org_default rows.
  const canPromote = hasMinRole('org_admin');

  /**
   * Fetches every parameter set visible to the current user from the backend.
   *
   * Wrapped in useCallback with an empty dep array so useEffect can depend on
   * a stable reference (prevents a fetch loop) AND so the modal confirm
   * handler can re-invoke the same function after a successful action to
   * refresh the list.
   *
   * Error path: we swallow the axios error object and keep only the
   * server-supplied `error` string (or a fallback). React can't render an
   * Error instance directly, so the `error` state must always be a string or
   * null.
   */
  const loadSets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listParameterSets();
      setSets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load parameter sets.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load. Re-runs only if loadSets identity changes, which it won't.
  useEffect(() => {
    loadSets();
  }, [loadSets]);

  /**
   * Client-side bucketing by visibility. The backend already filters by
   * access; we're only regrouping the already-visible rows so each section
   * (org_default / shared / private) can be rendered under its own header
   * in a single pass without re-scanning the full list per section.
   *
   * Defaults unknown visibility strings to 'private' as the most restrictive
   * bucket — defensive in case a new visibility is added server-side before
   * the UI is updated.
   */
  const grouped = sets.reduce((acc, set) => {
    const key = set.visibility || 'private';
    if (!acc[key]) acc[key] = [];
    acc[key].push(set);
    return acc;
  }, {});

  /**
   * True if the current user is the creator of the set.
   *
   * The dual-key check (`createdBy` || `created_by`) tolerates both the
   * camelCased response shape used by the REST serializer and the raw
   * snake_case shape used internally — this page historically had to handle
   * both during the migration rollout. Keep both until the API surface is
   * fully normalized.
   */
  const isOwner = (set) => set.createdBy === user?.id || set.created_by === user?.id;

  /**
   * True if the current user may edit/delete the given set.
   *
   *   - org_default sets: editable by org_admin (or higher) regardless of
   *                       who originally created them, because they
   *                       represent org-wide defaults managed by the admin.
   *   - private / shared sets: editable only by the creator.
   *
   * NOTE: this mirrors the backend canUserEdit() predicate exactly. If you
   * change the backend rule, update this function too — the backend will
   * still enforce correctly, but the UI will misleadingly show disabled
   * buttons.
   */
  const canEdit = (set) => {
    if (!set) return false;
    if (set.visibility === 'org_default') return canPromote;
    return isOwner(set);
  };

  /**
   * Dispatches the confirmed action (delete / share / unshare / promote)
   * against the set stored in the `confirm` state object, then reloads the
   * list on success.
   *
   * Failure path: we keep the modal OPEN and surface the error inside it
   * via `actionError`, so the user can see what went wrong and either
   * retry or cancel — this is important for the 403 "not your set" case
   * where the client-side predicate somehow allowed the button through
   * (shouldn't happen, but if it does the server is authoritative).
   */
  const performAction = async () => {
    if (!confirm) return;
    const { type, set } = confirm;
    setActionError(null);
    try {
      if (type === 'delete') {
        await deleteParameterSet(set.id);
      } else if (type === 'share') {
        await shareParameterSet(set.id);
      } else if (type === 'unshare') {
        await unshareParameterSet(set.id);
      } else if (type === 'promote') {
        await promoteParameterSet(set.id);
      }
      setConfirm(null);
      await loadSets();
    } catch (err) {
      setActionError(err.response?.data?.error || `Failed to ${type} parameter set.`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading parameter sets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-800">Parameter Sets</h1>
          <p className="mt-1 text-accent-500">
            Reusable collections of keywords and technical specs for RFP analysis.
          </p>
        </div>
        <Link to="/app/parameter-sets/new" className="btn-primary">
          <span className="mr-2">+</span>
          New Parameter Set
        </Link>
      </div>

      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {sets.length === 0 && !error ? (
        <div className="card px-6 py-12 text-center">
          <div className="text-4xl mb-4">🗂️</div>
          <h3 className="text-lg font-medium text-accent-800">No parameter sets yet</h3>
          <p className="mt-1 text-accent-500">
            Create a parameter set to reuse keywords and technical specs across analyses.
          </p>
          <Link to="/app/parameter-sets/new" className="btn-outline mt-4 inline-block">
            Create your first set
          </Link>
        </div>
      ) : (
        VISIBILITY_SECTIONS.map((section) => {
          const items = grouped[section.key] || [];
          if (items.length === 0) return null;

          return (
            <div key={section.key} className="card">
              <div className="px-6 py-4 border-b border-accent-200 bg-accent-50">
                <h2 className="text-lg font-semibold text-accent-800">{section.title}</h2>
                <p className="text-sm text-accent-500 mt-1">{section.description}</p>
              </div>
              <div className="divide-y divide-accent-100">
                {items.map((set) => {
                  const keywordCount = (set.keywords || []).length;
                  const specCount = (set.technicalSpecs || set.technical_specs || []).length;
                  const editable = canEdit(set);
                  const owned = isOwner(set);

                  return (
                    <div
                      key={set.id}
                      className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-accent-800 truncate">{set.name}</h3>
                          <span className="badge bg-accent-100 text-accent-600 text-xs">
                            {keywordCount} keywords
                          </span>
                          <span className="badge bg-accent-100 text-accent-600 text-xs">
                            {specCount} specs
                          </span>
                          {set.createdByName && !owned && (
                            <span className="text-xs text-accent-400">by @{set.createdByName}</span>
                          )}
                        </div>
                        {set.description && (
                          <p className="text-sm text-accent-500 mt-1 line-clamp-2">
                            {set.description}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 justify-end">
                        {editable && (
                          <button
                            type="button"
                            onClick={() => navigate(`/app/parameter-sets/${set.id}/edit`)}
                            className="text-xs px-3 py-1 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors"
                          >
                            Edit
                          </button>
                        )}

                        {owned && set.visibility === 'private' && (
                          <button
                            type="button"
                            onClick={() => setConfirm({ type: 'share', set })}
                            className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded hover:bg-accent-200 transition-colors"
                          >
                            Share
                          </button>
                        )}

                        {owned && set.visibility === 'shared' && (
                          <button
                            type="button"
                            onClick={() => setConfirm({ type: 'unshare', set })}
                            className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded hover:bg-accent-200 transition-colors"
                          >
                            Unshare
                          </button>
                        )}

                        {canPromote && set.visibility === 'shared' && (
                          <button
                            type="button"
                            onClick={() => setConfirm({ type: 'promote', set })}
                            className="text-xs px-3 py-1 bg-secondary-100 text-secondary-700 rounded hover:bg-secondary-200 transition-colors"
                          >
                            Promote to Default
                          </button>
                        )}

                        {editable && (
                          <button
                            type="button"
                            onClick={() => setConfirm({ type: 'delete', set })}
                            className="text-xs px-3 py-1 bg-error-light text-error-dark rounded hover:bg-error/20 transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* Confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-accent-800 mb-3">
              {confirm.type === 'delete' && 'Delete parameter set?'}
              {confirm.type === 'share' && 'Share with organization?'}
              {confirm.type === 'unshare' && 'Make private again?'}
              {confirm.type === 'promote' && 'Promote to organization default?'}
            </h3>
            <p className="text-accent-600 mb-4">
              {confirm.type === 'delete' &&
                'This will hide the set from your organization. Analyses that reference it will keep their stored values.'}
              {confirm.type === 'share' &&
                'All members of your organization will be able to use this set in their analyses.'}
              {confirm.type === 'unshare' && 'Only you will be able to see this set again.'}
              {confirm.type === 'promote' &&
                'This set will become an organization default, visible and selectable by every member.'}
            </p>
            <div className="bg-accent-50 rounded p-3 border border-accent-200 mb-4">
              <p className="font-medium text-accent-800">{confirm.set.name}</p>
            </div>

            {actionError && (
              <div className="mb-4 p-3 bg-error-light border border-error rounded-lg">
                <p className="text-sm text-error-dark">{actionError}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setConfirm(null);
                  setActionError(null);
                }}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performAction}
                className={
                  confirm.type === 'delete'
                    ? 'px-4 py-2 bg-error text-white rounded-lg hover:bg-error-dark transition-colors'
                    : 'btn-primary'
                }
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParameterSetsList;
